import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AppTopBar } from "@/components/AppTopBar";
import { formatIDR } from "@/components/AppTopBar";

const canView = (role) => role === "OWNER" || role === "ADMIN_INVENTORY";

export default function InventoryDetail() {
  const { productId } = useParams();
  const { user } = useAuth();
  const [info, setInfo] = useState(null);
  const [history, setHistory] = useState([]);
  const [dmgQty, setDmgQty] = useState("");
  const [dmgReason, setDmgReason] = useState("STORAGE_DAMAGE");
  const [dmgNote, setDmgNote] = useState("");
  const [dmgType, setDmgType] = useState("INTERNAL_DAMAGE");
  const [dmgPurchaseItem, setDmgPurchaseItem] = useState("");
  const [poItems, setPoItems] = useState([]);
  const [restoreQty, setRestoreQty] = useState("");
  const [restoreReason, setRestoreReason] = useState("WRONG_CLASSIFICATION");
  const [restoreNote, setRestoreNote] = useState("");
  const [returnQty, setReturnQty] = useState("");
  const [returnReason, setReturnReason] = useState("SUPPLIER_DEFECT");
  const [returnNote, setReturnNote] = useState("");
  const [returns, setReturns] = useState([]);
  const [writeoffs, setWriteoffs] = useState([]);
  const [woQty, setWoQty] = useState("");
  const [woReason, setWoReason] = useState("PHYSICAL_DAMAGE");
  const [woNote, setWoNote] = useState("");
  const [accounts, setAccounts] = useState([]);
  const [settleTarget, setSettleTarget] = useState(null);
  const [settleForm, setSettleForm] = useState({ refund_amount: "", account_id: "", reference: "" });
  const [error, setError] = useState("");
  const isOwner = user?.role === "OWNER";
  const canOperate = user && canView(user.role);

  const load = useCallback(() => {
    api.get(`/inventory/${productId}`).then(({ data }) => setInfo(data))
      .catch((err) => { console.error(err); setError(formatApiErrorDetail(err.response?.data?.detail)); });
    api.get(`/inventory/supplier-returns`, { params: { product_id: productId } })
      .then(({ data }) => setReturns(data)).catch(() => {});
    api.get(`/inventory/writeoffs`, { params: { product_id: productId } })
      .then(({ data }) => setWriteoffs(data)).catch(() => {});
    if (isOwner) {
      api.get(`/inventory/${productId}/valuation-history`).then(({ data }) => setHistory(data)).catch(() => {});
      api.get(`/accounts`).then(({ data }) => setAccounts(data)).catch(() => {});
    }
  }, [productId, isOwner]);
  useEffect(() => { load(); }, [load]);

  const doDamage = async () => {
    setError("");
    const qty = parseInt(dmgQty || "0", 10);
    if (qty <= 0) { setError("Qty harus lebih dari 0."); return; }
    if (!dmgReason) { setError("Alasan wajib diisi."); return; }
    if (dmgType === "QC_RECLASSIFICATION" && !dmgPurchaseItem) {
      setError("Reklasifikasi QC wajib memilih PO/receipt item asal."); return;
    }
    try {
      await api.post("/inventory/damage", {
        product_id: productId, qty, reason: dmgReason, note: dmgNote || null,
        reclass_type: dmgType,
        purchase_item_id: dmgType === "QC_RECLASSIFICATION" ? dmgPurchaseItem : null,
      });
      setDmgQty(""); setDmgNote(""); setDmgPurchaseItem(""); load();
    } catch (err) { console.error(err); setError(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const loadPoItems = () => {
    api.get(`/inventory/${productId}/purchase-items`).then(({ data }) => setPoItems(data)).catch(() => setPoItems([]));
  };

  const doRestore = async () => {
    setError("");
    const qty = parseInt(restoreQty || "0", 10);
    if (qty <= 0 || qty > (info?.damaged_qty ?? 0)) {
      setError("Qty harus lebih dari 0 dan tidak melebihi jumlah DAMAGED.");
      return;
    }
    if (!restoreReason) { setError("Alasan wajib diisi."); return; }
    if (!window.confirm(`Kembalikan ${qty} unit dari DAMAGED ke GOOD? Gunakan hanya untuk koreksi klasifikasi.`)) return;
    try {
      await api.post("/inventory/restore-good", { product_id: productId, qty, reason: restoreReason, note: restoreNote || null });
      setRestoreQty(""); setRestoreNote(""); load();
    } catch (err) { console.error(err); setError(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const doReturn = async () => {
    setError("");
    const qty = parseInt(returnQty || "0", 10);
    if (qty <= 0 || qty > (info?.damaged_qty ?? 0)) {
      setError("Qty harus lebih dari 0 dan tidak melebihi jumlah DAMAGED.");
      return;
    }
    if (!returnReason) { setError("Alasan wajib diisi."); return; }
    if (!window.confirm(`Anda akan mengeluarkan ${qty} unit DAMAGED dari inventory untuk dikembalikan ke supplier. Return akan berstatus menunggu penyelesaian.`)) return;
    try {
      await api.post("/inventory/supplier-return", { product_id: productId, qty, reason: returnReason, note: returnNote || null });
      setReturnQty(""); setReturnNote(""); load();
    } catch (err) { console.error(err); setError(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const openSettle = (r) => {
    setSettleForm({ refund_amount: r.inventory_value || "", account_id: accounts[0]?.account_id || "", reference: "" });
    setSettleTarget(r);
  };

  const submitSettle = async () => {
    setError("");
    const R = parseFloat(settleForm.refund_amount || "0");
    if (R <= 0 || !settleForm.account_id) { setError("Refund harus > 0 dan account tujuan wajib dipilih."); return; }
    const acct = accounts.find((a) => a.account_id === settleForm.account_id);
    const C = parseFloat(settleTarget.inventory_value || "0");
    const D = (R - C);
    const msg = `Supplier Return ${settleTarget.return_number} akan diselesaikan sebagai REFUND.\n` +
      `Dana ${formatIDR(R)} akan masuk ke account ${acct?.account_name || ""}.\n` +
      `Cost basis return ${formatIDR(C)}.\nSelisih settlement ${formatIDR(D)}.` +
      (D !== 0 ? `\n\nPERHATIAN: Refund berbeda dari cost basis inventory (selisih belum diklasifikasikan sebagai laba/rugi).` : "");
    if (!window.confirm(msg)) return;
    try {
      await api.post(`/supplier-returns/${settleTarget.id}/settle-refund`, {
        refund_amount: R, account_id: settleForm.account_id, reference: settleForm.reference || null,
      });
      setSettleTarget(null); load();
    } catch (err) { console.error(err); setError(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const markReplacement = async (r) => {
    setError("");
    const expected = window.prompt(`Set sebagai Penggantian Barang untuk ${r.return_number}.\nQty pengganti yang dijanjikan supplier (maks ${r.qty}):`, String(r.qty));
    if (expected === null) return;
    const q = parseInt(expected, 10);
    if (!(q > 0) || q > r.qty) { setError(`Qty penggantian harus 1..${r.qty}.`); return; }
    const reference = window.prompt("Reference/catatan supplier (opsional):", "") || null;
    try {
      await api.post(`/supplier-returns/${r.id}/mark-replacement`, { expected_qty: q, reference });
      load();
    } catch (err) { console.error(err); setError(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const receiveReplacement = async (r) => {
    setError("");
    const remaining = (r.replacement_expected_qty || 0) - (r.replacement_received_qty || 0);
    const val = window.prompt(`Terima Barang Pengganti — ${r.return_number}.\nBarang pengganti benar-benar sudah diterima secara fisik dan akan masuk ke GOOD dengan cost basis parkir return.\nQty diterima (sisa ${remaining}):`, String(remaining));
    if (val === null) return;
    const q = parseInt(val, 10);
    if (!(q > 0) || q > remaining) { setError(`Qty terima harus 1..${remaining}.`); return; }
    try {
      await api.post(`/inventory/supplier-returns/${r.id}/receive-replacement`, { qty: q });
      load();
    } catch (err) { console.error(err); setError(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const doWriteoff = async () => {
    setError("");
    const qty = parseInt(woQty || "0", 10);
    if (qty <= 0 || qty > (info?.damaged_qty ?? 0)) { setError("Qty harus lebih dari 0 dan tidak melebihi jumlah DAMAGED."); return; }
    if (!woReason) { setError("Reason wajib diisi."); return; }
    if (!window.confirm(`Anda akan melakukan WRITE-OFF ${qty} unit ${info.sku_code}.\nQty dan cost basis akan dikeluarkan dari inventory.\nTindakan ini tidak dapat di-undo pada V1.\nAccounting/P&L belum diposting otomatis.`)) return;
    try {
      await api.post("/inventory/writeoff", { product_id: productId, qty, reason: woReason, note: woNote || null });
      setWoQty(""); setWoNote(""); load();
    } catch (err) { console.error(err); setError(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  if (user && !canView(user.role)) {
    return (<div className="app-shell"><AppTopBar active="inventory" /><main className="content"><div className="alert-error" data-testid="access-denied">Akses ditolak.</div></main></div>);
  }

  return (
    <div className="app-shell">
      <AppTopBar active="inventory" />
      <main className="content">
        <Link to="/inventory" className="back-link" data-testid="back-to-list">← Kembali ke inventory</Link>
        {error && <div className="alert-error" data-testid="error-banner">{error}</div>}

        {info && (
          <>
            <section className="panel" data-testid="inventory-detail-panel">
              <div className="panel-head">
                <h2 className="panel-title">{info.sku_code}</h2>
                <span className={`status-pill ${info.status === "ACTIVE" ? "on" : "off"}`}>{info.status}</span>
              </div>
              <div className="meta-grid">
                <div><span className="meta-label">Available (GOOD)</span><span className="total-value" data-testid="available-qty">{info.available_qty}</span></div>
                <div><span className="meta-label">Damaged</span><span className="total-value" data-testid="damaged-qty">{info.damaged_qty}</span></div>
                {isOwner && <div><span className="meta-label">Avg Cost (GOOD)</span><span className="mono" data-testid="avg-cost">{info.good_avg_cost != null ? formatIDR(info.good_avg_cost) : (info.hpp_reference != null ? <span data-testid="hpp-ref"><span>{formatIDR(info.hpp_reference)}</span> <span className="tag-warn" style={{ marginLeft: 6 }}>HPP Referensi</span></span> : "belum diinisiasi")}</span></div>}
                {isOwner && <div><span className="meta-label">Nilai GOOD</span><span className="mono" data-testid="good-value">{info.cost_status === "UNINITIALIZED" ? "-" : formatIDR(info.good_value || 0)}</span></div>}
                {isOwner && <div><span className="meta-label">Nilai DAMAGED</span><span className="mono" data-testid="damaged-value">{formatIDR(info.damaged_value || 0)}</span></div>}
              </div>
            </section>

            {info.stock_source === "COMPONENT" && (
              <section className="panel" data-testid="component-physical-lock-note">
                <p className="hint">SKU Komponen — stok fisik dikelola melalui SKU sumber. Aksi mutasi fisik (GOOD ↔ DAMAGED, Return, Write-off) dinonaktifkan untuk SKU ini.</p>
              </section>
            )}

            {info.stock_source !== "COMPONENT" && (<section className="panel" data-testid="damage-panel">
              <h2 className="panel-title">Tandai Rusak (GOOD → DAMAGED)</h2>
              <p className="hint">Memindahkan qty + nilai modal (pada rata-rata GOOD saat ini) ke bucket DAMAGED. Tidak boleh melebihi GOOD.</p>
              <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 8, flexWrap: "wrap" }}>
                <select className="field-input narrow" value={dmgType} data-testid="damage-type-select"
                  onChange={(e) => { const v = e.target.value; setDmgType(v); setDmgReason(v === "QC_RECLASSIFICATION" ? "DEFECTIVE_ON_ARRIVAL" : "STORAGE_DAMAGE"); if (v === "QC_RECLASSIFICATION") loadPoItems(); else setDmgPurchaseItem(""); }}>
                  <option value="INTERNAL_DAMAGE">Kerusakan Internal (tidak ubah Purchase)</option>
                  <option value="QC_RECLASSIFICATION">Reklasifikasi QC (tanggung jawab supplier)</option>
                </select>
                {dmgType === "QC_RECLASSIFICATION" && (
                  <select className="field-input" value={dmgPurchaseItem} onChange={(e) => setDmgPurchaseItem(e.target.value)}
                    data-testid="damage-po-item-select" style={{ maxWidth: 320 }}>
                    <option value="">-- pilih PO/receipt asal --</option>
                    {poItems.map((it) => (
                      <option key={it.purchase_item_id} value={it.purchase_item_id}>
                        {it.purchase_no} — Accepted {it.accepted_good_qty}
                      </option>
                    ))}
                  </select>
                )}
                <input className="field-input narrow" type="number" min="1" placeholder="qty" value={dmgQty}
                  onChange={(e) => setDmgQty(e.target.value)} data-testid="damage-qty-input" />
                <select className="field-input narrow" value={dmgReason} onChange={(e) => setDmgReason(e.target.value)} data-testid="damage-reason-select">
                  {dmgType === "QC_RECLASSIFICATION" ? (
                    <>
                      <option value="DEFECTIVE_ON_ARRIVAL">Defect Saat Diterima</option>
                      <option value="SUPPLIER_DEFECT">Defect Supplier</option>
                      <option value="WRONG_ITEM_SENT">Salah Kirim Supplier</option>
                      <option value="QC_RECLASSIFICATION">Reklasifikasi QC</option>
                    </>
                  ) : (
                    <>
                      <option value="STORAGE_DAMAGE">Rusak Penyimpanan</option>
                      <option value="HANDLING_DAMAGE">Rusak Handling</option>
                      <option value="PACKAGING_DAMAGE">Rusak Kemasan</option>
                      <option value="ACCIDENT">Kecelakaan Gudang</option>
                      <option value="OTHER">Lainnya</option>
                    </>
                  )}
                </select>
                <input className="field-input" placeholder="catatan (opsional)" value={dmgNote}
                  onChange={(e) => setDmgNote(e.target.value)} data-testid="damage-note-input" style={{ maxWidth: 220 }} />
                <button className="btn-primary" onClick={doDamage} disabled={!dmgQty} data-testid="damage-submit-button">Pindahkan ke DAMAGED</button>
              </div>
              {dmgType === "QC_RECLASSIFICATION" && (
                <p className="hint" data-testid="qc-hint" style={{ marginTop: 6 }}>QC: mengurangi Accepted dan menambah Replacement Pending pada PO terpilih. Delivery Outstanding tidak berubah.</p>
              )}
            </section>)}

            {info.stock_source !== "COMPONENT" && (<section className="panel" data-testid="restore-good-panel">
              <h2 className="panel-title">Kembalikan ke GOOD (DAMAGED → GOOD)</h2>
              <p className="hint">Gunakan untuk koreksi klasifikasi / barang yang setelah pemeriksaan ulang dinyatakan baik. Qty tidak boleh melebihi DAMAGED ({info.damaged_qty}).</p>
              <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 8, flexWrap: "wrap" }}>
                <input className="field-input narrow" type="number" min="1" max={info.damaged_qty} placeholder="qty" value={restoreQty}
                  onChange={(e) => setRestoreQty(e.target.value)} data-testid="restore-qty-input" />
                <select className="field-input narrow" value={restoreReason} onChange={(e) => setRestoreReason(e.target.value)} data-testid="restore-reason-select">
                  <option value="WRONG_CLASSIFICATION">Salah Klasifikasi</option>
                  <option value="PASSED_REINSPECTION">Lolos Pemeriksaan Ulang</option>
                  <option value="REPACK_REPAIR_SUCCESS">Repack/Perbaikan Berhasil</option>
                  <option value="OTHER">Lainnya</option>
                </select>
                <input className="field-input" placeholder="catatan (opsional)" value={restoreNote}
                  onChange={(e) => setRestoreNote(e.target.value)} data-testid="restore-note-input" style={{ maxWidth: 220 }} />
                <button className="btn-primary" onClick={doRestore} disabled={!restoreQty || info.damaged_qty === 0} data-testid="restore-submit-button">Kembalikan ke GOOD</button>
              </div>
            </section>)}

            {info.stock_source !== "COMPONENT" && (<section className="panel" data-testid="supplier-return-panel">
              <h2 className="panel-title">Return ke Supplier (DAMAGED → Supplier)</h2>
              <p className="hint">Untuk barang DAMAGED yang benar-benar keluar fisik dan dikembalikan ke supplier. Nilai modal keluar dari inventory dan menunggu penyelesaian (belum refund/loss/replacement). Qty tidak boleh melebihi DAMAGED ({info.damaged_qty}).</p>
              <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 8, flexWrap: "wrap" }}>
                <input className="field-input narrow" type="number" min="1" max={info.damaged_qty} placeholder="qty" value={returnQty}
                  onChange={(e) => setReturnQty(e.target.value)} data-testid="return-qty-input" />
                <select className="field-input narrow" value={returnReason} onChange={(e) => setReturnReason(e.target.value)} data-testid="return-reason-select">
                  <option value="SUPPLIER_DEFECT">Cacat dari Supplier</option>
                  <option value="WRONG_ITEM">Barang Salah</option>
                  <option value="FACTORY_DAMAGE">Rusak Pabrik</option>
                  <option value="SUPPLIER_AGREEMENT">Kesepakatan Supplier</option>
                  <option value="OTHER">Lainnya</option>
                </select>
                <input className="field-input" placeholder="catatan (opsional)" value={returnNote}
                  onChange={(e) => setReturnNote(e.target.value)} data-testid="return-note-input" style={{ maxWidth: 220 }} />
                <button className="btn-primary" onClick={doReturn} disabled={!returnQty || info.damaged_qty === 0} data-testid="return-submit-button">Konfirmasi Return</button>
              </div>
            </section>)}

            <section className="panel" data-testid="supplier-returns-list-panel">
              <h2 className="panel-title">Return ke Supplier ({returns.length})</h2>
              <div className="table-scroll">
                <table className="data-table" data-testid="supplier-returns-table">
                  <thead>
                    <tr><th>Return No</th><th>Tanggal</th><th>SKU</th><th>Qty</th>{isOwner && <th>Cost Basis</th>}{isOwner && <th>Refund</th>}<th>Referensi</th><th>Status</th>{canOperate && <th>Aksi</th>}</tr>
                  </thead>
                  <tbody>
                    {returns.length === 0 && <tr><td colSpan={4 + (isOwner ? 2 : 0) + 2 + (canOperate ? 1 : 0)} className="empty-cell" data-testid="returns-empty">Belum ada return ke supplier.</td></tr>}
                    {returns.map((r) => (
                      <tr key={r.id} data-testid={`return-row-${r.return_number}`}>
                        <td className="mono">{r.return_number}</td>
                        <td>{new Date(r.created_at).toLocaleString("id-ID")}</td>
                        <td className="mono">{r.sku_code || "-"}</td>
                        <td className="mono">{r.qty}</td>
                        {isOwner && <td className="mono">{formatIDR(r.inventory_value || 0)}</td>}
                        {isOwner && <td className="mono" data-testid={`refund-${r.return_number}`}>{r.status === "REFUNDED" ? formatIDR(r.refund_amount || 0) : "-"}</td>}
                        <td>{r.purchase_id ? "Purchase" : "-"}</td>
                        <td><span className={`status-pill ${r.status === "REFUNDED" || r.status === "REPLACED" ? "on" : "off"}`} data-testid={`return-status-${r.return_number}`}>{
                          r.status === "REFUNDED" ? "Refunded"
                          : r.status === "REPLACED" ? "Replaced"
                          : r.status === "REPLACEMENT_PENDING" ? `Menunggu Barang Pengganti (${r.replacement_received_qty || 0}/${r.replacement_expected_qty || 0})`
                          : "Menunggu Penyelesaian"
                        }</span></td>
                        {canOperate && (
                          <td className="actions-cell">
                            {isOwner && r.status === "PENDING_SETTLEMENT" && (
                              <>
                                <button className="btn-mini" onClick={() => openSettle(r)} data-testid={`settle-${r.return_number}`}>Selesaikan sebagai Refund</button>
                                <button className="btn-mini" onClick={() => markReplacement(r)} data-testid={`mark-replacement-${r.return_number}`}>Set sebagai Penggantian Barang</button>
                              </>
                            )}
                            {r.status === "REPLACEMENT_PENDING" && (
                              <button className="btn-mini" onClick={() => receiveReplacement(r)} data-testid={`receive-replacement-${r.return_number}`}>Terima Barang Pengganti</button>
                            )}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            {isOwner && settleTarget && (
              <section className="panel" data-testid="settle-modal">
                <h2 className="panel-title">Selesaikan sebagai Refund — {settleTarget.return_number}</h2>
                <p className="hint">Cost basis return: <strong>{formatIDR(settleTarget.inventory_value || 0)}</strong>. Masukkan dana refund yang benar-benar diterima dan pilih account tujuan. Inventory tidak berubah.</p>
                <div className="grid-2" style={{ marginTop: 8 }}>
                  <div>
                    <label className="field-label">Refund diterima *</label>
                    <input className="field-input" type="number" min="0" step="0.01" value={settleForm.refund_amount}
                      onChange={(e) => setSettleForm({ ...settleForm, refund_amount: e.target.value })} data-testid="settle-amount-input" />
                  </div>
                  <div>
                    <label className="field-label">Account tujuan *</label>
                    <select className="field-input" value={settleForm.account_id}
                      onChange={(e) => setSettleForm({ ...settleForm, account_id: e.target.value })} data-testid="settle-account-select">
                      <option value="">-- pilih account --</option>
                      {accounts.map((a) => <option key={a.account_id} value={a.account_id}>{a.account_name} ({a.account_type})</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="field-label">Reference / catatan</label>
                    <input className="field-input" value={settleForm.reference}
                      onChange={(e) => setSettleForm({ ...settleForm, reference: e.target.value })} data-testid="settle-reference-input" />
                  </div>
                </div>
                {settleForm.refund_amount !== "" && (
                  <p className="hint" data-testid="settle-diff" style={{ marginTop: 8 }}>
                    Selisih settlement: <strong>{formatIDR((parseFloat(settleForm.refund_amount || "0") - parseFloat(settleTarget.inventory_value || "0")))}</strong>
                    {parseFloat(settleForm.refund_amount || "0") - parseFloat(settleTarget.inventory_value || "0") !== 0 && " — berbeda dari cost basis (belum diklasifikasikan laba/rugi)."}
                  </p>
                )}
                <div className="form-actions" style={{ marginTop: 10 }}>
                  <button className="btn-primary" onClick={submitSettle} data-testid="settle-confirm-button">Konfirmasi Refund</button>
                  <button className="btn-ghost" onClick={() => setSettleTarget(null)} data-testid="settle-cancel-button">Batal</button>
                </div>
              </section>
            )}

            {info.stock_source !== "COMPONENT" && (<section className="panel" data-testid="writeoff-panel">
              <h2 className="panel-title">Write-off Barang Rusak (DAMAGED → Write-off)</h2>
              <p className="hint">Gunakan untuk barang rusak permanen yang tidak akan dikembalikan ke supplier dan harus dikeluarkan dari inventory. Qty tidak boleh melebihi DAMAGED ({info.damaged_qty}).</p>
              {isOwner ? (
                <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 8, flexWrap: "wrap" }}>
                  <input className="field-input narrow" type="number" min="1" max={info.damaged_qty} placeholder="qty" value={woQty}
                    onChange={(e) => setWoQty(e.target.value)} data-testid="writeoff-qty-input" />
                  <select className="field-input narrow" value={woReason} onChange={(e) => setWoReason(e.target.value)} data-testid="writeoff-reason-select">
                    <option value="PHYSICAL_DAMAGE">Rusak Fisik</option>
                    <option value="EXPIRED">Kadaluarsa</option>
                    <option value="LOST_UNUSABLE">Hilang / Tidak Layak</option>
                    <option value="OTHER">Lainnya</option>
                  </select>
                  <input className="field-input" placeholder="catatan (opsional)" value={woNote}
                    onChange={(e) => setWoNote(e.target.value)} data-testid="writeoff-note-input" style={{ maxWidth: 240 }} />
                  <button className="btn-primary" onClick={doWriteoff} disabled={!woQty || info.damaged_qty === 0} data-testid="writeoff-submit-button">Konfirmasi Write-off</button>
                </div>
              ) : (
                <p className="hint" data-testid="writeoff-owner-only">Hanya OWNER yang dapat mengeksekusi write-off.</p>
              )}
            </section>)}

            <section className="panel" data-testid="writeoffs-list-panel">
              <h2 className="panel-title">Riwayat Write-off ({writeoffs.length})</h2>
              <div className="table-scroll">
                <table className="data-table" data-testid="writeoffs-table">
                  <thead>
                    <tr><th>Write-off No</th><th>Tanggal</th><th>SKU</th><th>Qty</th><th>Reason</th>{isOwner && <th>Cost Basis</th>}<th>Status</th></tr>
                  </thead>
                  <tbody>
                    {writeoffs.length === 0 && <tr><td colSpan={isOwner ? 7 : 6} className="empty-cell" data-testid="writeoffs-empty">Belum ada write-off.</td></tr>}
                    {writeoffs.map((w) => (
                      <tr key={w.id} data-testid={`writeoff-row-${w.writeoff_number}`}>
                        <td className="mono">{w.writeoff_number}</td>
                        <td>{new Date(w.created_at).toLocaleString("id-ID")}</td>
                        <td className="mono">{w.sku_code || "-"}</td>
                        <td className="mono">{w.qty}</td>
                        <td>{w.reason}</td>
                        {isOwner && <td className="mono">{formatIDR(w.inventory_value || 0)}</td>}
                        <td><span className="status-pill off">Pending Accounting</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            {isOwner && (
              <section className="panel" data-testid="valuation-history-panel">
                <h2 className="panel-title">Riwayat Valuasi ({history.length})</h2>
                <div className="table-scroll">
                  <table className="data-table" data-testid="valuation-history-table">
                    <thead>
                      <tr><th>Waktu</th><th>Sumber</th><th>Bucket</th><th>Qty Δ</th><th>Nilai Δ</th><th>Qty Akhir</th><th>Nilai Akhir</th><th>Alasan</th><th>Catatan</th><th>User</th><th>Ref</th></tr>
                    </thead>
                    <tbody>
                      {history.length === 0 && <tr><td colSpan={11} className="empty-cell">Belum ada event valuasi.</td></tr>}
                      {history.map((e) => (
                        <tr key={e.id} data-testid={`val-event-${e.id}`}>
                          <td>{new Date(e.created_at).toLocaleString("id-ID")}</td>
                          <td><span className="status-pill on">{e.source_type}</span></td>
                          <td>{e.bucket}</td>
                          <td className="mono">{e.qty_effect}</td>
                          <td className="mono">{formatIDR(e.value_effect)}</td>
                          <td className="mono">{e.new_qty}</td>
                          <td className="mono">{formatIDR(e.new_value)}</td>
                          <td data-testid={`val-reason-${e.id}`}>{e.reason || "-"}</td>
                          <td>{e.note || "-"}</td>
                          <td className="mono">{e.actor_id ? e.actor_id.slice(0, 8) : "-"}</td>
                          <td className="mono">{e.source_ref ? e.source_ref.slice(0, 8) : "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            )}
          </>
        )}
      </main>
    </div>
  );
}
